import { Component, inject, OnDestroy, PLATFORM_ID, ViewChild } from "@angular/core";
import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { RouterLink } from "@angular/router";
import { SettingsService } from "../../../services/settings/settings.service";
import { AuthLayoutComponent } from "../../ui/auth-layout/auth-layout.component";

/** Seconds before the same address can ask for another link. */
const RESEND_COOLDOWN_S = 60;

interface ResetFeedback {
    title: string;
    message: string;
}

@Component({
    selector: 'resetpassword-form',
    standalone: true,
    templateUrl: './resetpasswordForm.component.html',
    imports: [ReactiveFormsModule, RouterLink, AuthLayoutComponent]
})

export class ResetpasswordForm implements OnDestroy {
    settingsService = inject(SettingsService);
    private document = inject(DOCUMENT);
    private platformId = inject(PLATFORM_ID);

    @ViewChild(AuthLayoutComponent) private layout?: AuthLayoutComponent;

    resetpasswordForm = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email])
    })

    successful = false
    isLoading: boolean = false
    submitted = false

    /** The address the link went to, kept for "Resend link". */
    sentTo = ''
    /** Seconds left before "Resend link" works again. */
    cooldown = 0
    /** When the last resend went out, for "Sent again at 14:32". */
    resentAt: Date | null = null
    isResending = false

    // Feedback (errors only; success moves to the "Check your inbox" state)
    feedback: ResetFeedback | null = null

    private cooldownTimer?: ReturnType<typeof setInterval>;

    // get email form control for validation
    get email() {
        return this.resetpasswordForm.get('email')
    }

    get showEmailError(): boolean {
        const control = this.email;
        return !!control?.invalid && ((control.dirty && control.touched) || this.submitted);
    }

    get emailErrorMessage(): string {
        return this.email?.hasError('required')
            ? 'Enter the email address on your account.'
            : 'Enter an email address like name@company.com.';
    }

    get heading(): string {
        return this.successful ? 'Check your inbox' : 'Reset your password';
    }

    get sub(): string {
        return this.successful
            ? `If an account uses ${this.sentTo}, a link to set a new password is on its way.`
            : 'Enter the email on your account and we’ll send you a link to set a new password.';
    }

    get resentTime(): string {
        if (!this.resentAt) return '';
        return this.resentAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    // submit the form
    handleSubmit() {
        if (this.isLoading) return;
        this.submitted = true;

        const email = (this.resetpasswordForm.value.email ?? '').trim();
        if (email !== this.resetpasswordForm.value.email) {
            this.email?.setValue(email);
        }

        if (this.resetpasswordForm.invalid) {
            this.resetpasswordForm.markAllAsTouched();
            this.focusField('reset-email');
            return;
        }

        this.isLoading = true;
        this.feedback = null;

        this.settingsService.requestPasswordReset(email).subscribe({
            next: () => {
                this.isLoading = false;
                this.successful = true;
                this.sentTo = email;
                this.resentAt = null;
                this.resetpasswordForm.reset({ email: '' });
                this.submitted = false;
                this.startCooldown();
                this.layout?.focusHeading();
            },
            error: (err) => {
                this.isLoading = false;
                this.feedback = this.describeError(err);
            }
        });
    }

    /** Send the link again to the same address, once the cooldown is over. */
    resend() {
        if (this.cooldown > 0 || this.isResending || !this.sentTo) return;
        this.isResending = true;
        this.feedback = null;

        this.settingsService.requestPasswordReset(this.sentTo).subscribe({
            next: () => {
                this.isResending = false;
                this.resentAt = new Date();
                this.startCooldown();
            },
            error: (err) => {
                this.isResending = false;
                this.feedback = this.describeError(err);
                // A rate limit lasts an hour; don't invite an immediate retry.
                if (err?.status === 429) this.startCooldown();
            }
        });
    }

    /** Back to the form with the address filled in, to fix a typo. */
    useDifferentEmail() {
        this.successful = false;
        this.feedback = null;
        this.resentAt = null;
        this.resetpasswordForm.reset({ email: this.sentTo });
        this.focusField('reset-email');
    }

    private describeError(err: any): ResetFeedback {
        const status: number | undefined = err?.status;
        const body = err?.error;
        const raw = [body?.message, body?.title, body?.detail, body?.errorKey, typeof body === 'string' ? body : '']
            .filter((part) => typeof part === 'string')
            .join(' ');

        if (status === 429) {
            return {
                title: 'Too many reset requests',
                message: 'You can ask for 4 links an hour. Use the newest email you have, or try again in an hour.',
            };
        }
        if (/not.?verified/i.test(raw)) {
            return {
                title: 'This email address isn’t verified yet',
                message: 'Ask an administrator in your organization to resend your account invitation.',
            };
        }
        if (/disabled/i.test(raw)) {
            return {
                title: 'This account is turned off',
                message: 'Ask an administrator in your organization to turn it back on.',
            };
        }
        if (status === 0) {
            return {
                title: 'Can’t reach the server',
                message: 'Check your internet connection, then try again.',
            };
        }
        if (typeof status === 'number' && status >= 500) {
            return {
                title: 'We couldn’t send the link',
                message: 'The server had a problem. Try again in a few minutes.',
            };
        }
        const serverMessage = typeof body?.message === 'string' && !/^error\./.test(body.message) ? body.message : '';
        return {
            title: 'We couldn’t send the link',
            message: serverMessage || 'Something went wrong. Try again.',
        };
    }

    private startCooldown() {
        this.stopCooldown();
        if (!isPlatformBrowser(this.platformId)) return;
        this.cooldown = RESEND_COOLDOWN_S;
        this.cooldownTimer = setInterval(() => {
            this.cooldown = Math.max(0, this.cooldown - 1);
            if (this.cooldown === 0) this.stopCooldown();
        }, 1000);
    }

    private stopCooldown() {
        if (this.cooldownTimer) {
            clearInterval(this.cooldownTimer);
            this.cooldownTimer = undefined;
        }
    }

    private focusField(id: string) {
        if (!isPlatformBrowser(this.platformId)) return;
        setTimeout(() => this.document.getElementById(id)?.focus());
    }

    ngOnDestroy(): void {
        this.stopCooldown();
    }
}
